import React from 'react';
import { useLazyLoadQuery, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { ff } from '@atlassian/jira-feature-flagging';
import FetchError from '@atlassian/jira-fetch/src/utils';
import {
	ISSUE_AGG_OPERATION_NAME,
	extractProjectKey,
	getMainIssueViewAggQueryFeatureFlagVariables,
	issueAggQuery,
} from '@atlassian/jira-issue-fetch-services-common';
import { ISSUE_CRITICAL_DATA_AGG_SUSPENSE } from '@atlassian/jira-issue-view-common-constants/src/mark-types';
import {
	type ResourceManager,
	useLazyLoadingResourceManager,
} from '@atlassian/jira-issue-view-common-utils/src/utils/prefetched-resources';
import { IssueViewFetchError } from '@atlassian/jira-issue-view-errors';
import JiraRelayEnvironmentProvider from '@atlassian/jira-relay-environment-provider';
import type { AGGError } from '@atlassian/jira-relay-errors';
import type {
	mainIssueAggQuery as mainIssueAggQueryType,
	mainIssueAggQuery$data as mainIssueAggQueryResponse,
} from '@atlassian/jira-relay/src/__generated__/mainIssueAggQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { getTraceIds } from '@atlassian/relay-traceid';
import { blockedOnBackendEntryLog } from '../../../../common/metrics/reporters/backend-blocking-time-reporter';
import { useIssueRouteResourceManager } from './route-resources';

const extractFirstErrorFromRelayError = (error: Error) => {
	if (!('source' in error)) {
		return null;
	}

	const errorSource = error.source;
	const errors =
		typeof errorSource === 'object' &&
		errorSource != null &&
		'errors' in errorSource &&
		errorSource.errors;
	const firstError: AGGError | null = Array.isArray(errors) ? errors[0] : null;

	const statusCode = firstError?.extensions?.statusCode;
	const message = firstError?.message;
	return statusCode ? { statusCode, message } : null;
};

/**
 * Handler for errors caught when using a Relay hook
 * @throws original error with an added traceId
 * @param err the caught error
 */
const updateAndRethrowRelayQueryError = (err: unknown): void => {
	if (!(err instanceof Error)) {
		return;
	}
	const issueAggQueryTraceIds = getTraceIds(ISSUE_AGG_OPERATION_NAME);
	const lastTraceIdIndex = issueAggQueryTraceIds.length - 1;
	const traceId = issueAggQueryTraceIds[lastTraceIdIndex];

	const error = extractFirstErrorFromRelayError(err);
	if (error) {
		throw new IssueViewFetchError(
			new FetchError(error.statusCode, error.message),
			'',
			traceId,
			true,
		);
	} else {
		throw new IssueViewFetchError(err, 'RelayErrorWithoutStatusCode', traceId, true);
	}
};

/**
 * See {@link IssueViewCriticalQueryPreloader}
 */
const IssueViewCriticalQueryPreloaderInner = ({
	issueKey,
	children,
}: {
	issueKey: string;
	children: (childrenProps: {
		prefetchedResourceManager: ResourceManager;
		issueAggQueryData: mainIssueAggQueryResponse;
	}) => JSX.Element;
}) => {
	blockedOnBackendEntryLog.initialiseBlockedOnBackendStateForUpdatedKey(issueKey);
	blockedOnBackendEntryLog.markStartBackendBlockingTimestamp(ISSUE_CRITICAL_DATA_AGG_SUSPENSE);
	const cloudId = useCloudId();
	const projectKey = extractProjectKey(issueKey);

	let issueAggQueryData;
	let prefetchedResourceManager;
	try {
		// This will internally kick off a relay fetch in the render phase (alongside a call to gira)
		prefetchedResourceManager = useLazyLoadingResourceManager(issueKey);
		// We expect Relay to internally dedupe AGG fetches from useLazyLoadingResourceManager with this useLazyLoadQuery
		// This will then suspend on the AGG response
		issueAggQueryData = useLazyLoadQuery<mainIssueAggQueryType>(
			issueAggQuery,
			{
				cloudId,
				issueKey,
				projectKey,
				...getMainIssueViewAggQueryFeatureFlagVariables(),
			},
			{ fetchPolicy: 'store-and-network' },
		);
	} catch (err) {
		if (ff('issue.view-add-traceids-to-issue-view-relay-errors_vm8h2')) {
			updateAndRethrowRelayQueryError(err);
		}
		throw err;
	}

	blockedOnBackendEntryLog.markEndBackendBlockingTimestamp(ISSUE_CRITICAL_DATA_AGG_SUSPENSE);

	return children({ issueAggQueryData, prefetchedResourceManager });
};

/**
 * Wrapper component for the {@link AppProvider} which takes a children-render-function
 * and provides it with a relay fragment and prefetchedResourceManager.
 * Triggers loading of issue view data fetches as soon as the issueKey changes.
 * Throws suspense for some of the data (AGG, not the gira fetch).
 *
 * Alternative to {@link IssueViewRouteResourceConsumer}, this is for use in
 * issue-view apps without route-resources wired up.
 * */
export const IssueViewCriticalQueryPreloader = (props: {
	issueKey: string;
	children: (childrenProps: {
		prefetchedResourceManager: ResourceManager;
		issueAggQueryData: mainIssueAggQueryResponse;
	}) => JSX.Element;
}) => (
	<JiraRelayEnvironmentProvider>
		<IssueViewCriticalQueryPreloaderInner {...props} />
	</JiraRelayEnvironmentProvider>
);

/**
 * Wrapper component for the {@link AppProvider} which takes a children-render-function
 * and provides it with a relay fragment and prefetchedResourceManager.
 * Subscribes to route-resources for issue view data.
 * Throws suspense for some of the data (AGG, not the gira fetch).
 *
 * Alternative to {@link IssueViewCriticalQueryPreloader}, this will not preload data
 * if the route resources it subscribes to are not wired up.
 * */
export const IssueViewRouteResourceConsumer = ({
	issueKey,
	preloadedQuery,
	children,
}: {
	issueKey: string;
	preloadedQuery: PreloadedQuery<mainIssueAggQueryType>;
	children: (props: {
		prefetchedResourceManager: ResourceManager;
		issueAggQueryData: mainIssueAggQueryResponse;
	}) => JSX.Element;
}) => {
	blockedOnBackendEntryLog.initialiseBlockedOnBackendStateForUpdatedKey(issueKey);
	blockedOnBackendEntryLog.markStartBackendBlockingTimestamp(ISSUE_CRITICAL_DATA_AGG_SUSPENSE);
	let issueAggQueryData;
	try {
		// This will trigger suspense if the data has not yet arrived.
		issueAggQueryData = usePreloadedQuery<mainIssueAggQueryType>(issueAggQuery, preloadedQuery);
	} catch (err) {
		if (ff('issue.view-add-traceids-to-issue-view-relay-errors_vm8h2')) {
			updateAndRethrowRelayQueryError(err);
		}
		throw err;
	}
	blockedOnBackendEntryLog.markEndBackendBlockingTimestamp(ISSUE_CRITICAL_DATA_AGG_SUSPENSE);
	const prefetchedResourceManager = useIssueRouteResourceManager({
		issueKey,
		preloadedQuery,
	});

	return children({ issueAggQueryData, prefetchedResourceManager });
};
